import { useEffect } from "react";
import { AlertCircle, Trash2 } from "lucide-react";

/**
 * ConfirmDialog — modal de confirmação reutilizável do SobeVenda
 *
 * Uso:
 *   <ConfirmDialog
 *     open={confirmOpen}
 *     title="Remover produto"
 *     message="Tem certeza? Esta ação não pode ser desfeita."
 *     confirmLabel="Remover"       // opcional, default: "Confirmar"
 *     cancelLabel="Cancelar"       // opcional, default: "Cancelar"
 *     destructive={true}           // opcional, default: true
 *     onConfirm={() => handleDelete(id)}
 *     onCancel={() => setConfirmOpen(false)}
 *   />
 *
 * Salve em: src/components/ConfirmDialog.jsx
 */
export default function ConfirmDialog({
  open,
  title,
  message,
  confirmLabel = "Confirmar",
  cancelLabel = "Cancelar",
  destructive = true,
  onConfirm,
  onCancel,
}) {
  // Fecha com ESC
  useEffect(() => {
    if (!open) return;
    const handleKey = (e) => { if (e.key === "Escape") onCancel(); };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [open, onCancel]);

  // Bloqueia scroll do body enquanto aberto
  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" aria-modal="true" role="dialog">
      {/* Overlay — clica fora pra fechar */}
      <div className="absolute inset-0 bg-black/50 dark:bg-black/70" onClick={onCancel} />

      {/* Dialog */}
      <div className="relative z-10 w-full max-w-sm rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-6 shadow-lg">
        {/* Ícone + Título */}
        <div className="flex items-start gap-3 mb-3">
          <AlertCircle className="w-5 h-5 text-red-500 dark:text-red-400 mt-0.5 shrink-0" />
          <h2 className="text-base font-bold text-slate-900 dark:text-slate-100">{title}</h2>
        </div>

        {/* Mensagem */}
        <p className="text-sm text-slate-600 dark:text-slate-300 mb-6 leading-relaxed pl-8">
          {message}
        </p>

        {/* Ações */}
        <div className="flex items-center gap-3">
          <button
            onClick={onCancel}
            className="flex-1 inline-flex items-center justify-center px-4 py-2 rounded-lg border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 text-sm"
          >
            {cancelLabel}
          </button>

          <button
            onClick={onConfirm}
            className={`flex-1 inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold ${
              destructive
                ? "border border-red-200 dark:border-red-900 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20"
                : "bg-slate-900 text-white hover:bg-slate-800 dark:bg-white dark:text-slate-900 dark:hover:bg-slate-100"
            }`}
          >
            {destructive && <Trash2 className="w-4 h-4" />}
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}